import React from 'react';
import { getCycleColor } from './colorUtils';
import type { ColorSet } from './colorUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FishboneCategory {
  name: string;
  causes: string[];
}

interface FishboneDiagramProps {
  title?: string;
  effect: string;
  categories: FishboneCategory[];
}

// ---------------------------------------------------------------------------
// Layout constants
// ---------------------------------------------------------------------------

const SVG_WIDTH = 900;
const SVG_HEIGHT = 500;

const SPINE_Y = 250;
const SPINE_X_START = 60;
const SPINE_X_END = 750;

const EFFECT_BOX_X = SPINE_X_END;
const EFFECT_BOX_WIDTH = 140;
const EFFECT_BOX_HEIGHT = 60;
const EFFECT_BOX_Y = SPINE_Y - EFFECT_BOX_HEIGHT / 2;

const BRANCH_ANGLE_DEG = 35;
const BRANCH_LENGTH = 140;
const CAUSE_LINE_LENGTH = 70;

const CATEGORY_LABEL_WIDTH = 110;
const CATEGORY_LABEL_HEIGHT = 26;

// ---------------------------------------------------------------------------
// Geometry helpers
// ---------------------------------------------------------------------------

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Wrap text into lines that fit within a given character width. */
function wrapText(text: string, maxCharsPerLine: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (testLine.length > maxCharsPerLine && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** The effect box on the right side of the spine. */
const EffectBox: React.FC<{ effect: string }> = ({ effect }) => {
  const lines = wrapText(effect, 18);
  const lineHeight = 14;
  const totalTextHeight = lines.length * lineHeight;
  const startY = EFFECT_BOX_Y + (EFFECT_BOX_HEIGHT - totalTextHeight) / 2 + lineHeight - 2;

  return (
    <g>
      <rect
        x={EFFECT_BOX_X}
        y={EFFECT_BOX_Y}
        width={EFFECT_BOX_WIDTH}
        height={EFFECT_BOX_HEIGHT}
        rx={6}
        ry={6}
        fill="#DC2626"
        stroke="#991B1B"
        strokeWidth={1.5}
      />
      {lines.map((line, i) => (
        <text
          key={i}
          x={EFFECT_BOX_X + EFFECT_BOX_WIDTH / 2}
          y={startY + i * lineHeight}
          textAnchor="middle"
          fill="white"
          fontSize={11}
          fontWeight={700}
        >
          {line}
        </text>
      ))}
    </g>
  );
};

/** The central spine line. */
const Spine: React.FC = () => (
  <g>
    <line
      x1={SPINE_X_START}
      y1={SPINE_Y}
      x2={SPINE_X_END}
      y2={SPINE_Y}
      stroke="#374151"
      strokeWidth={2.5}
    />
    {/* Arrowhead pointing right */}
    <polygon
      points={`${SPINE_X_END},${SPINE_Y - 6} ${SPINE_X_END + 10},${SPINE_Y} ${SPINE_X_END},${SPINE_Y + 6}`}
      fill="#374151"
    />
  </g>
);

/** A single category branch with its causes. */
const CategoryBranch: React.FC<{
  category: FishboneCategory;
  index: number;
  totalCategories: number;
  colorSet: ColorSet;
}> = ({ category, index, totalCategories, colorSet }) => {
  const isAbove = index % 2 === 0;
  const direction = isAbove ? -1 : 1;

  // Distribute attachment points evenly along the spine
  const usableSpineLength = SPINE_X_END - SPINE_X_START - 80;
  const spacing = totalCategories > 1 ? usableSpineLength / totalCategories : usableSpineLength / 2;
  const attachX = SPINE_X_START + 40 + spacing * index + spacing / 2;

  const angleRad = degToRad(BRANCH_ANGLE_DEG);
  const branchEndX = attachX - Math.cos(angleRad) * BRANCH_LENGTH;
  const branchEndY = SPINE_Y + direction * Math.sin(angleRad) * BRANCH_LENGTH;

  // Category label position (at the end of the branch)
  const labelX = branchEndX - CATEGORY_LABEL_WIDTH / 2;
  const labelY = isAbove
    ? branchEndY - CATEGORY_LABEL_HEIGHT - 4
    : branchEndY + 4;

  // Cause lines branch off the main branch at regular intervals
  const causes = category.causes || [];
  const maxCauses = Math.min(causes.length, 6);

  return (
    <g>
      {/* Main branch line */}
      <line
        x1={attachX}
        y1={SPINE_Y}
        x2={branchEndX}
        y2={branchEndY}
        stroke={colorSet.stroke}
        strokeWidth={2}
      />

      {/* Category label box */}
      <rect
        x={labelX}
        y={labelY}
        width={CATEGORY_LABEL_WIDTH}
        height={CATEGORY_LABEL_HEIGHT}
        rx={4}
        ry={4}
        fill={colorSet.fill}
        stroke={colorSet.stroke}
        strokeWidth={1}
      />
      <text
        x={labelX + CATEGORY_LABEL_WIDTH / 2}
        y={labelY + CATEGORY_LABEL_HEIGHT / 2 + 4}
        textAnchor="middle"
        fill="white"
        fontSize={11}
        fontWeight={600}
      >
        {category.name}
      </text>

      {/* Cause lines */}
      {causes.slice(0, maxCauses).map((cause, ci) => {
        // Position each cause along the branch, starting near the tip
        const t = (ci + 1) / (maxCauses + 1);
        const causeAttachX = branchEndX + (attachX - branchEndX) * t;
        const causeAttachY = branchEndY + (SPINE_Y - branchEndY) * t;

        // Cause line extends horizontally to the left from the branch
        const causeEndX = causeAttachX - CAUSE_LINE_LENGTH;
        const causeEndY = causeAttachY;

        // Slight upward/downward angle for cause lines to make them look natural
        const causeAngleOffset = direction * 8;

        return (
          <g key={ci}>
            <line
              x1={causeAttachX}
              y1={causeAttachY}
              x2={causeEndX}
              y2={causeEndY + causeAngleOffset}
              stroke={colorSet.fill}
              strokeWidth={1.2}
              opacity={0.8}
            />
            {/* Small circle at attachment point */}
            <circle
              cx={causeAttachX}
              cy={causeAttachY}
              r={2.5}
              fill={colorSet.fill}
            />
            {/* Cause text label */}
            <text
              x={causeEndX - 4}
              y={causeEndY + causeAngleOffset + 4}
              textAnchor="end"
              fill="#374151"
              fontSize={10}
            >
              {cause.length > 28 ? cause.slice(0, 26) + '...' : cause}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const FishboneDiagram: React.FC<FishboneDiagramProps> = ({
  title,
  effect,
  categories,
}) => {
  if (!categories || categories.length === 0) {
    return null;
  }

  const resolvedColors: ColorSet[] = categories.map((_, i) => getCycleColor(i));

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden my-8">
      {/* Optional title header */}
      {title && (
        <div className="border-b border-gray-200 px-5 py-3 bg-gray-50">
          <h3 className="text-base font-semibold text-gray-800 m-0">
            {title}
          </h3>
        </div>
      )}

      {/* Diagram */}
      <div className="p-4 md:p-6">
        <svg
          viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
          width="100%"
          height="auto"
          role="img"
          aria-label={
            title
              ? `Fishbone diagram: ${title}`
              : `Fishbone diagram showing causes of: ${effect}`
          }
          style={{ maxWidth: SVG_WIDTH }}
        >
          {/* Spine */}
          <Spine />

          {/* Effect box */}
          <EffectBox effect={effect} />

          {/* Category branches */}
          {categories.map((category, index) => (
            <CategoryBranch
              key={index}
              category={category}
              index={index}
              totalCategories={categories.length}
              colorSet={resolvedColors[index]}
            />
          ))}
        </svg>
      </div>
    </div>
  );
};

export default FishboneDiagram;
